"use client";

import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  CheckCircleIcon,
  CheckCircleOnlyIcon,
} from "staysure-component-library";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { ANNUAL_MULTI_TRIP, SINGLE_TRIP } from "@/constants";
import { RootState } from "@/store/root-reducer";
import { PolicyPackagePrice } from "@/types/PolicyPackagePriceType";

import styles from "./price-toggle.module.css";

type PriceToggleProps = {
  cardType: string;
  currentTripType: string;
  onToggle: (tripType: string) => void;
  isDisabled?: boolean;
};

function PriceToggle({
  cardType,
  currentTripType,
  onToggle,
  isDisabled = false,
}: Readonly<PriceToggleProps>) {
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { current } = useWizard();

  const priceData = useMemo(() => {
    const singleTripCover = attributes?.priceResult?.policyPackagePrice?.find(
      (cover: PolicyPackagePrice) =>
        cover?.policyType === SINGLE_TRIP && cover?.policyPackage === cardType
    );

    const annualCover = attributes?.priceResult?.policyPackagePrice?.find(
      (cover: PolicyPackagePrice) =>
        cover?.policyType === ANNUAL_MULTI_TRIP &&
        cover?.policyPackage === cardType
    );

    const formatPrice = (cover: PolicyPackagePrice | undefined) => {
      if (!cover) return "£0.00";
      const price =
        cover?.onlinePresentationTotalWithDiscount ?? cover?.total ?? 0;
      return `${
        (current?.brandSpecificParams?.currencySymbol as string) ?? "£"
      }${price?.toFixed(2)}`;
    };

    const currencySymbol =
      (current?.brandSpecificParams?.currencySymbol as string) ?? "£";

    return {
      singleTrip: formatPrice(singleTripCover),
      annualMultiTrip: formatPrice(annualCover),
      singleTripCover,
      annualCover,
      currencySymbol,
    };
  }, [attributes?.priceResult?.policyPackagePrice, cardType, current]);

  const handleToggleClick = useCallback(
    (tripType: string) => {
      if (!isDisabled && tripType !== currentTripType) {
        onToggle(tripType);
      }
    },
    [isDisabled, currentTripType, onToggle]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, tripType: string) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleToggleClick(tripType);
      }
    },
    [handleToggleClick]
  );

  const hasSingleTripDeclined =
    priceData?.singleTripCover?.hasMedicalDeclined === true;
  const hasAnnualDeclined = priceData?.annualCover?.hasMedicalDeclined === true;

  // If both are declined, don't render the toggle (card should be hidden at parent level)
  if (hasSingleTripDeclined && hasAnnualDeclined) {
    return null;
  }

  const hasMonthlyDisplay =
    priceData?.annualCover &&
    priceData?.annualCover?.totalCost !== null &&
    priceData?.annualCover?.totalCost !== undefined;

  return (
    <div
      className={`${styles.wrapper} ${
        hasMonthlyDisplay ? styles.wrapperTall : ""
      }`}
      data-testid="price-toggle-wrapper"
    >
      {!hasSingleTripDeclined && (
        <div
          className={`${styles.option} ${
            currentTripType === SINGLE_TRIP ? styles.active : ""
          }`}
          data-trip-type={SINGLE_TRIP}
          role="button"
          tabIndex={0}
          aria-pressed={currentTripType === SINGLE_TRIP}
          onClick={() => handleToggleClick(SINGLE_TRIP)}
          onKeyDown={(e) => handleKeyDown(e, SINGLE_TRIP)}
          data-testid="price-toggle-single-trip"
        >
          <div className={styles.checkmark}>
            {currentTripType === SINGLE_TRIP ? (
              <CheckCircleIcon fill="#FFFFFF" stroke="#306582" />
            ) : (
              <CheckCircleOnlyIcon fill="#FFFFFF" stroke="#306582" />
            )}
          </div>
          <div className={styles.content}>
            <div className={styles.label}>Single trip</div>
            <div className={styles.value}>
              {priceData?.singleTrip}{" "}
              <span className={styles.perYear}>per trip</span>
            </div>
          </div>
        </div>
      )}

      {!hasAnnualDeclined && (
        <div
          className={`${styles.option} ${
            currentTripType === ANNUAL_MULTI_TRIP ? styles.active : ""
          } ${hasMonthlyDisplay ? styles.tallOption : ""}`}
          data-trip-type={ANNUAL_MULTI_TRIP}
          role="button"
          tabIndex={0}
          aria-pressed={currentTripType === ANNUAL_MULTI_TRIP}
          onClick={() => handleToggleClick(ANNUAL_MULTI_TRIP)}
          onKeyDown={(e) => handleKeyDown(e, ANNUAL_MULTI_TRIP)}
          data-testid="price-toggle-annual-trip"
        >
          <div className={styles.checkmark}>
            {currentTripType === ANNUAL_MULTI_TRIP ? (
              <CheckCircleIcon fill="#FFFFFF" stroke="#306582" />
            ) : (
              <CheckCircleOnlyIcon fill="#FFFFFF" stroke="#306582" />
            )}
          </div>
          <div className={styles.content}>
            <div className={styles.label}>Multi trip</div>
            {priceData?.annualCover &&
            priceData?.annualCover?.totalCost !== null &&
            priceData?.annualCover?.totalCost !== undefined ? (
              <div className="flex flex-col gap-1 md:gap-0">
                <div className={styles.value}>
                  {priceData?.annualMultiTrip}{" "}
                  <span className={styles.perYear}>per year</span>
                </div>
                <div
                  className={`${styles.monthlyValueColor} text-[14px] font-normal`}
                >
                  <span className={styles.perYear}>or</span>{" "}
                  <span className="font-bold">
                    {priceData?.currencySymbol}
                    {priceData?.annualCover?.collectionAmount?.toFixed(2)}
                  </span>{" "}
                  per month
                </div>
              </div>
            ) : (
              <div className={styles.value}>
                {priceData?.annualMultiTrip}{" "}
                <span className={styles.perYear}>per year</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PriceToggle;
